import BaseEntity from 'client/components/core/BaseEntity/BaseEntityNew'
import EntityExampleWrapper from 'client/components/core/EntityExampleWrapper'
import { pricePlansStub } from 'client/constants/editorSettings'
import { useIsShowAdditionalAction } from 'client/hooks/use-is-show-additional-action'
import usePageRouteDetector from 'client/hooks/usePageRouteDetector'
import usePayment from 'client/hooks/usePayment'
import { getIsFetching, getPricePlans } from 'client/reducers/paymentReducer'
import { usePage, usePayment as useTypedPayment } from 'client/store'
import pageSelectors from 'client/store/page/pageSelectors'
import typedPaymentSelectors from 'client/store/payment/paymentSelectors'
import { EntityProps } from 'client/types'
import translatePricePlans from 'client/utils/translatePriceStub'
import { OfferPriceInterface } from 'common/types/entities/OfferPriceInterface'
import { PricePlan } from 'common/types/OfferInterface'
import React from 'react'
import { useOfferPricing } from 'src/editor/hooks/use-offer-pricing'
import { OfferPrice as CommonOfferPrice } from 'src/shared/components/offer-price'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'

export const OfferPrice = ({
  entity,
  isMoveUpAllowed,
  isMoveDownAllowed,
}: EntityProps<OfferPriceInterface>) => {
  const isFetching = usePayment(getIsFetching)
  const pricePlans: PricePlan[] = usePayment(getPricePlans)
  const pageLocale = usePage(pageSelectors.getPageLocale)
  const { isTemplate } = usePageRouteDetector()
  const entityParents = usePage(page =>
    pageSelectors.getEntityParents(page.entities, entity),
  )
  const product = useTypedPayment(typedPaymentSelectors.getProduct)
  const { i18n } = useTypedTranslation()
  const fixedT = i18n.getFixedT(pageLocale, ['client', 'common'])

  const {
    physicalMainOfferPricing,
    physicalBumpOfferPricing,
    digitalBumpOfferPricing,
    digitalOfferPricings,
  } = useOfferPricing()

  // @TODO add more detailed example, only basic price is displayed in client atm
  const total = 0

  const isShowAdditionalAction = useIsShowAdditionalAction({
    entityParents,
    entity,
  })

  if (isFetching && !isTemplate) {
    return null
  }

  const isExample = pricePlans.length === 0 && !product
  const calculatedPricePlans: PricePlan[] = isExample
    ? translatePricePlans(pricePlansStub, fixedT)
    : pricePlans

  const renderOfferPrice = (entity: OfferPriceInterface) => {
    return (
      <CommonOfferPrice
        attrId={entity.htmlAttrId}
        mainOfferPricing={physicalMainOfferPricing}
        bumpOfferPricing={physicalBumpOfferPricing}
        pricePlansPricing={digitalOfferPricings}
        bumpPlanPricing={digitalBumpOfferPricing}
        plans={calculatedPricePlans}
        activePlanId={calculatedPricePlans[0]?.id}
        customDescription={entity.customDescription}
        product={product}
        total={total}
        togglePricePlan={() => null}
        locale={pageLocale}
        offerLineUiProps={{
          nameFontFamily: entity.nameFontFamily,
          mobileNameFontFamily: entity.mobileNameFontFamily,
          nameFontSize: entity.nameFontSize,
          mobileNameFontSize: entity.mobileNameFontSize,
          nameFontStyle: entity.nameFontStyle,
          mobileNameFontStyle: entity.mobileNameFontStyle,
          nameFontWeight: entity.nameFontWeight,
          mobileNameFontWeight: entity.mobileNameFontWeight,
          nameLineHeight: entity.nameLineHeight,
          mobileNameLineHeight: entity.mobileNameLineHeight,
          nameColor: entity.nameColor,
          mobileNameColor: entity.mobileNameColor,
          amountFontFamily: entity.amountFontFamily,
          mobileAmountFontFamily: entity.mobileAmountFontFamily,
          amountFontSize: entity.amountFontSize,
          mobileAmountFontSize: entity.mobileAmountFontSize,
          amountFontStyle: entity.amountFontStyle,
          mobileAmountFontStyle: entity.mobileAmountFontStyle,
          amountFontWeight: entity.amountFontWeight,
          mobileAmountFontWeight: entity.mobileAmountFontWeight,
          amountLineHeight: entity.amountLineHeight,
          mobileAmountLineHeight: entity.mobileAmountLineHeight,
          amountColor: entity.amountColor,
          mobileAmountColor: entity.mobileAmountColor,
          descriptionFontFamily: entity.descriptionFontFamily,
          mobileDescriptionFontFamily: entity.mobileDescriptionFontFamily,
          descriptionFontSize: entity.descriptionFontSize,
          mobileDescriptionFontSize: entity.mobileDescriptionFontSize,
          descriptionFontStyle: entity.descriptionFontStyle,
          mobileDescriptionFontStyle: entity.mobileDescriptionFontStyle,
          descriptionFontWeight: entity.descriptionFontWeight,
          mobileDescriptionFontWeight: entity.mobileDescriptionFontWeight,
          descriptionLineHeight: entity.descriptionLineHeight,
          mobileDescriptionLineHeight: entity.mobileDescriptionLineHeight,
          descriptionColor: entity.descriptionColor,
          mobileDescriptionColor: entity.mobileDescriptionColor,
        }}
      />
    )
  }

  return (
    <BaseEntity
      entity={entity}
      isMoveUpAllowed={isMoveUpAllowed}
      isMoveDownAllowed={isMoveDownAllowed}
      isSaveAllowed={isShowAdditionalAction}
      isRemoveAllowed={isShowAdditionalAction}
      isCopyAllowed={isShowAdditionalAction}
    >
      {isExample ? (
        <EntityExampleWrapper label="entities.offer_price.example_warning">
          {renderOfferPrice(entity)}
        </EntityExampleWrapper>
      ) : (
        renderOfferPrice(entity)
      )}
    </BaseEntity>
  )
}
